import { ChevronLeftIcon } from '@heroicons/react/16/solid'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button } from '../catalyst/button'
import { Checkbox, CheckboxField, CheckboxGroup } from '../catalyst/checkbox'
import { Description, Field as FormField, FieldGroup, Fieldset, Label, Legend } from '../catalyst/fieldset'
import { Heading, Subheading } from '../catalyst/heading'
import { Input } from '../catalyst/input'
import { Link } from '../catalyst/link'
import { RadioCards } from '../catalyst/radio-cards'
import { Steps } from '../catalyst/steps'
import { Text } from '../catalyst/text'
import { formatArea, orgAreaUnit } from '../lib/area'
import type { AreaUnit, Field } from '../lib/types'
import { useAuth } from '../lib/auth'
import { useEntryCheck, type CanadaGapCheckRequest, type CanadaGapCheckResult } from '../lib/canadagap'
import { ComplianceBadge } from '../lib/compliance-badge'
import { checkBadgeProps } from '../farmlogs/entry-check'
import { useFarms } from '../lib/farms'
import { useFields } from '../lib/fields'
import { Shell } from '../pages/shell'
import { flowFor } from './flows/registry'
import { entriesOf, fieldIdsOf, forField, todayIso, str, type FlowDef, type FlowEntry, type StepCtx, type WizardStep } from './flow'
import { isTransient } from './draft-sync'
import { ActivityChoices } from './log-start-page'
import { problemMessage } from './messages'
import { useDraft, type Values } from './use-draft'

/** Ticking fields for an entry that covers several, with their total area. */
function FieldChecklist({ fields, areaUnit, selected, onChange }: { fields: Field[]; areaUnit: AreaUnit; selected: string[]; onChange: (ids: string[]) => void }) {
  const all = fields.length > 1 && fields.every((f) => selected.includes(f.id))
  const total = fields.filter((f) => selected.includes(f.id)).reduce((sum, f) => sum + f.areaM2, 0)
  // Kept in the farm's order, whatever order they were ticked in.
  const set = (id: string, on: boolean) => onChange(fields.map((f) => f.id).filter((x) => (x === id ? on : selected.includes(x))))
  return (
    <Fieldset>
      <Legend>Which fields?</Legend>
      <CheckboxGroup className="mt-3">
        {fields.length > 1 && (
          <CheckboxField className="rounded-lg px-4 py-3 ring-1 ring-zinc-950/10 dark:ring-white/10">
            <Checkbox checked={all} indeterminate={!all && selected.length > 0} onChange={(on) => onChange(on ? fields.map((f) => f.id) : [])} />
            <Label>All fields</Label>
          </CheckboxField>
        )}
        {fields.map((f) => (
          <CheckboxField key={f.id} className="rounded-lg px-4 py-3 ring-1 ring-zinc-950/10 dark:ring-white/10">
            <Checkbox checked={selected.includes(f.id)} onChange={(on) => set(f.id, on)} />
            <Label>{f.name}</Label>
            <Description>{formatArea(f.areaM2, areaUnit)}</Description>
          </CheckboxField>
        ))}
      </CheckboxGroup>
      {selected.length > 1 && (
        <Text className="mt-3">
          {selected.length} fields · {formatArea(total, areaUnit)}. Each field gets its own record.
        </Text>
      )}
    </Fieldset>
  )
}

/** The first step of every flow: which field or fields (field-based logs) and which day. */
function whereWhenStep(flow: FlowDef): WizardStep {
  const { needsField, severalFields } = flow
  return {
    id: 'where-when',
    title: needsField ? 'Where and when?' : 'Which farm and when?',
    valid: (v) => str(v.farmId) !== '' && str(v.date) !== '' && (!needsField || fieldIdsOf(v).length > 0),
    render: ({ farms, farmFixed, setFarm, farmId, fields, areaUnit, values, patch }) => {
      const open = fields.filter((f) => !f.archivedAt)
      return (
        <FieldGroup>
          {!farmFixed && farms.length > 1 && (
            <RadioCards label="Which farm?" value={farmId} options={farms.map((f) => ({ value: f.id, label: f.name }))} onChange={setFarm} />
          )}
          {needsField && farmId && severalFields && (
            <FieldChecklist fields={open} areaUnit={areaUnit} selected={fieldIdsOf(values)} onChange={(ids) => patch({ fieldIds: ids, fieldId: undefined })} />
          )}
          {needsField && farmId && !severalFields && (
            <RadioCards
              label="Which field?"
              value={str(values.fieldId)}
              options={open.map((f) => ({ value: f.id, label: f.name, detail: formatArea(f.areaM2, areaUnit) }))}
              onChange={(id) => patch({ fieldId: id })}
            />
          )}
          <FormField>
            <Label>When?</Label>
            <Input type="date" value={str(values.date)} max={todayIso(farms.find((f) => f.id === farmId)?.timezone)} onChange={(e) => patch({ date: e.target.value })} />
          </FormField>
        </FieldGroup>
      )
    },
  }
}

/** Harvest-timing problems are shown as warnings above, with their own acknowledgement. */
const SHOWN_AS_WARNINGS = new Set(['harvest_before_eahd', 'eahd_unknown'])

/** What CanadaGAP still needs from this entry, in the form's own words. Never blocks finishing. */
function CheckSummary({ result, formCode }: { result: CanadaGapCheckResult | null; formCode: string }) {
  if (!result) return null
  const violations = result.violations.filter((v) => !SHOWN_AS_WARNINGS.has(v.code))
  if (result.missing.length === 0 && violations.length === 0) {
    return <Text className="text-emerald-700 dark:text-emerald-400">✓ Nothing missing for CanadaGAP {formCode}</Text>
  }
  return (
    <div className="space-y-2 rounded-lg bg-amber-50 p-4 ring-1 ring-amber-600/20 dark:bg-amber-900/20 dark:ring-amber-500/20">
      {violations.map((v) => (
        <p key={v.code + v.message} className="text-sm/6 text-amber-900 dark:text-amber-200">⚠ {v.message}</p>
      ))}
      {result.missing.length > 0 && (
        <>
          <p className="text-sm/6 font-medium text-amber-900 dark:text-amber-200">CanadaGAP {formCode} still needs:</p>
          <ul className="list-disc space-y-1 pl-5 text-sm/6 text-amber-900 dark:text-amber-200">
            {result.missing.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </>
      )}
      <p className="text-xs/5 text-zinc-600 dark:text-zinc-400">
        You can still finish. Use “Change” above to fill these in, or the entry stays flagged in the records.
      </p>
    </div>
  )
}

/** The CanadaGAP check of one log the entry will record, with its badge. */
function EntryCheck({ orgId, farmId, entry, heading }: { orgId: string; farmId: string; entry: FlowEntry & { checkForm: NonNullable<FlowEntry['checkForm']> }; heading?: string }) {
  const request: CanadaGapCheckRequest =
    entry.checkForm === 'p2' ? { form: 'p2', fieldId: entry.fieldId ?? '', entry: entry.body } : { form: entry.checkForm, entry: entry.body }
  const check = useEntryCheck(orgId, farmId, request)
  return (
    <div className="space-y-3">
      {heading && <Subheading level={3}>{heading}</Subheading>}
      {entry.badge && <CheckSummary result={check} formCode={entry.badge.formCode} />}
      {entry.badge && (
        <div className="flex">
          <ComplianceBadge {...entry.badge} {...checkBadgeProps(check)} />
        </div>
      )}
    </div>
  )
}

const hasCheck = (e: FlowEntry): e is FlowEntry & { checkForm: NonNullable<FlowEntry['checkForm']> } => !!e.checkForm

/** Runs one flow: where/when, the flow's own steps, then review. Progress is kept automatically. */
export function WizardRunner({ flow }: { flow: FlowDef }) {
  const { farmId: farmParam, draftId } = useParams<{ farmId: string; draftId?: string }>()
  const [search] = useSearchParams()
  const navigate = useNavigate()
  const { org } = useAuth()
  const orgId = org?.id ?? ''
  const areaUnit = orgAreaUnit(org)
  const { farms } = useFarms()
  // The farm comes from the address, or is chosen on the first step (an only farm is chosen for you).
  const [picked, setPicked] = useState('')
  const farmId = farmParam ?? (picked || (farms.length === 1 ? farms[0]!.id : ''))
  const logBase = farmParam ? `/farms/${farmParam}/log` : '/log'
  const { fields } = useFields(org?.id, farmId)

  const initialValues = useMemo(() => {
    // `?field=` (from a field's page) or `?fields=a,b&date=` (carried over from the last entry).
    const seeded = (search.get('fields') ?? search.get('field') ?? '').split(',').filter(Boolean)
    const today = todayIso(farms.find((f) => f.id === farmId)?.timezone)
    const date = search.get('date') ?? undefined
    const values: Values = { ...flow.initial({ today, fieldId: seeded[0] }), farmId }
    if (date && date <= today) values.date = date
    if (flow.severalFields) {
      delete values.fieldId
      values.fieldIds = seeded
    }
    return values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow, search])
  const draft = useDraft({ orgId, farmId, kind: flow.kind, draftId: draftId ?? null, initialValues })
  const { values, patch, ready } = draft

  // An entry taken back from this browser (typed offline from /log) remembers its farm.
  useEffect(() => {
    if (ready && !farmParam && !picked && typeof values.farmId === 'string' && values.farmId) setPicked(values.farmId)
  }, [ready, farmParam, picked, values.farmId])

  // Keep the entry's farm in its values (the step checks it there, and older drafts lack it).
  useEffect(() => {
    if (ready && farmId && values.farmId !== farmId) patch({ farmId })
  }, [ready, farmId, values.farmId, patch])

  function setFarm(id: string) {
    setPicked(id)
    patch({ farmId: id, fieldId: '', fieldIds: [], perField: {} })
  }

  const [last, setLast] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [warnings, setWarnings] = useState<string[]>([])
  const [acknowledged, setAcknowledged] = useState(false)
  /** The warning check is still running: finishing waits so a warning can't be skipped unseen. */
  const [checking, setChecking] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [done, setDone] = useState<FlowEntry[] | null>(null)

  const fieldIds = fieldIdsOf(values)
  const fieldKey = fieldIds.join(',')
  const selectedFields = fields.filter((f) => fieldIds.includes(f.id))
  const field = selectedFields[0]
  const fieldId = field?.id

  useEffect(() => {
    if (!flow.fetchLast || !orgId || (flow.needsField && !fieldId)) return
    let cancelled = false
    flow.fetchLast(orgId, farmId, fieldId).then(
      (found) => !cancelled && setLast(found),
      () => !cancelled && setLast(null),
    )
    return () => {
      cancelled = true
    }
  }, [flow, orgId, farmId, fieldId])

  const ctx: StepCtx = { orgId, farmId, farms, farmFixed: !!farmParam, setFarm, fields, field, selectedFields, areaUnit, values, patch, last }

  // Warnings (a harvest inside a waiting period) are checked for each field whenever the review
  // shows. A new set of warnings needs a new acknowledgement.
  const reviewing = ready && draft.step >= flow.steps.length + 1
  useEffect(() => {
    setAcknowledged(false)
    const warn = flow.warnings
    if (!reviewing || !warn) {
      setWarnings([])
      setChecking(false)
      return
    }
    let cancelled = false
    setChecking(true)
    const targets = flow.needsField ? selectedFields.map((f) => ({ name: f.name, c: forField(ctx, f) })) : [{ name: '', c: ctx }]
    Promise.all(targets.map(({ name, c }) => warn(c).then((found) => found.map((w) => (targets.length > 1 ? `${name}: ${w}` : w))))).then(
      (found) => {
        if (cancelled) return
        setWarnings(found.flat())
        setChecking(false)
      },
      () => {
        // Advisory: if the check can't run, recording still works.
        if (cancelled) return
        setWarnings([])
        setChecking(false)
      },
    )
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewing, flow, orgId, farmId, fieldKey, fields, values.date])

  const steps = useMemo<WizardStep[]>(
    () => [whereWhenStep(flow), ...flow.steps, { id: 'review', title: 'Review', valid: () => true, render: () => null }],
    [flow],
  )
  const step = Math.min(draft.step, steps.length - 1)
  const current = steps[step]!
  const isReview = step === steps.length - 1

  if (!org) return null
  if (draft.loadError) {
    return (
      <Shell>
        <Text>{draft.loadError}</Text>
        <Link href={logBase}>Back to logging</Link>
      </Shell>
    )
  }
  if (!ready) return <Shell><Text>Opening…</Text></Shell>

  if (done) {
    // One link per place the entry went (a field's records page per field and kind).
    const places = [...new Map(done.map((e) => [e.historyPath, e])).values()]
    const nameOf = (e: FlowEntry) => fields.find((f) => f.id === e.fieldId)?.name
    // The next entry starts on the same day and fields: its first step is already answered.
    const carry = new URLSearchParams({ date: str(values.date), ...(fieldIds.length ? { fields: fieldIds.join(',') } : {}) })
    return (
      <Shell>
        <div className="mx-auto max-w-xl space-y-8">
          <div className="space-y-3 text-center">
            <Heading>Logged</Heading>
            <Text>
              {flow.review(ctx).title} is in your records{done.length > 1 ? ` (${done.length} records)` : ''}.
            </Text>
          </div>
          <ActivityChoices heading="Log something else for today?" exclude={flow.kind} onChoose={(kind) => navigate(`/farms/${farmId}/log/${kind}?${carry}`)} />
          <div className="flex flex-col gap-3">
            {places.map((e) => (
              <Button key={e.historyPath} outline href={e.historyPath}>
                {places.length > 1 && nameOf(e) ? `See ${[nameOf(e), e.badge?.formCode].filter(Boolean).join(' · ')} in the records` : 'See it in the records'}
              </Button>
            ))}
            {flow.after?.(ctx).map((a) => (
              <Button key={a.href} outline href={a.href}>
                {a.label}
              </Button>
            ))}
            <Button plain href={`/farms/${farmId}`}>Back to farm</Button>
          </div>
        </div>
      </Shell>
    )
  }

  async function next() {
    setFailure(null)
    const to = step + 1
    draft.setStep(to)
    window.scrollTo({ top: 0 })
    // The first step is done: from here on there is something worth keeping on the server. (Moved on
    // first, so the draft is created already at the next step.)
    if (step === 0 && !draftId) {
      try {
        const id = await draft.ensureCreated()
        // Now that the farm is known, the address names it. Opened from /log this loads the draft again.
        navigate(`/farms/${farmId}/log/${flow.kind}/${id}`, { replace: true })
      } catch (err) {
        // Offline: keep going; the entry is kept on this device and sent when the connection returns.
        // Anything else (no permission, too many unfinished entries) is an answer: stay and say so.
        if (!isTransient(err)) {
          draft.setStep(step)
          setFailure(problemMessage(err, 'start this entry'))
        }
      }
    }
  }

  function back() {
    if (step === 0) navigate(logBase)
    else draft.setStep(step - 1)
    window.scrollTo({ top: 0 })
  }

  // On review, the same CanadaGAP check the Add/Edit dialogs run, on exactly the bodies that will be saved.
  const entries = isReview ? entriesOf(flow, ctx) : []
  const checked = entries.filter(hasCheck)
  const headingFor = (e: FlowEntry) =>
    checked.length > 1 ? [fields.find((f) => f.id === e.fieldId)?.name, e.badge && `${e.badge.framework} ${e.badge.formCode}`].filter(Boolean).join(' · ') : undefined

  async function finish() {
    setBusy(true)
    setFailure(null)
    try {
      await draft.commit(entries.map((e) => ({ kind: e.kind, entry: e.body })))
      setDone(entries)
      flow.onRecorded?.(ctx).catch(() => {})
    } catch (err) {
      setFailure(problemMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const review = isReview ? flow.review(ctx) : null

  return (
    <Shell>
      <div className="mx-auto flex min-h-[calc(100dvh-8rem)] max-w-xl flex-col">
        <Link href={logBase} className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
          <ChevronLeftIcon className="size-4" />
          Log
        </Link>
        <Steps current={step} total={steps.length} className="mt-4" />
        <div className="mt-6 flex-1 space-y-6">
          <Heading>{isReview ? 'Check and finish' : current.title}</Heading>
          {!isReview && current.render(ctx)}
          {review && (
            <div className="space-y-4">
              <Subheading>{review.title}</Subheading>
              <dl className="divide-y divide-zinc-950/5 rounded-lg ring-1 ring-zinc-950/10 dark:divide-white/10 dark:ring-white/10">
                {review.lines.map((line, i) => (
                  <div key={i} className="flex justify-between gap-4 px-4 py-3 text-sm/6">
                    <dt className="text-zinc-500 dark:text-zinc-400">{line.label}</dt>
                    <dd className="text-right font-medium text-zinc-950 dark:text-white">{line.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="flex flex-wrap gap-x-4">
                {steps.slice(0, -1).map((s, i) => (
                  <Button key={s.id} plain onClick={() => draft.setStep(i)}>
                    Change: {s.title}
                  </Button>
                ))}
              </div>
              {checked.map((e, i) => (
                <EntryCheck key={`${e.kind}:${e.fieldId ?? ''}:${i}`} orgId={orgId} farmId={farmId} entry={e} heading={headingFor(e)} />
              ))}
              {warnings.length > 0 && (
                <div role="alert" className="space-y-3 rounded-lg bg-amber-50 p-4 ring-1 ring-amber-600/20 dark:bg-amber-900/20 dark:ring-amber-500/20">
                  {warnings.map((w) => (
                    <p key={w} className="text-sm/6 text-amber-800 dark:text-amber-300">⚠ {w}</p>
                  ))}
                  <CheckboxField className="py-2">
                    <Checkbox checked={acknowledged} onChange={setAcknowledged} />
                    <Label className="font-medium">This is what happened. Record it anyway; it stays flagged in the records.</Label>
                  </CheckboxField>
                </div>
              )}
            </div>
          )}
        </div>
        {failure && (
          <Text role="alert" className="mt-6 text-red-600 dark:text-red-400">
            {failure}
          </Text>
        )}
        <div className="sticky bottom-0 -mx-4 mt-8 flex gap-3 border-t border-zinc-950/10 bg-white/90 px-4 py-3 backdrop-blur dark:border-white/10 dark:bg-zinc-900/90">
          <Button outline className="min-h-11 flex-1" onClick={back}>Back</Button>
          {isReview ? (
            <Button className="min-h-11 flex-1" disabled={busy || checking || entries.length === 0 || (warnings.length > 0 && !acknowledged)} onClick={finish}>
              {busy ? 'Working…' : 'Done'}
            </Button>
          ) : (
            <Button className="min-h-11 flex-1" disabled={!current.valid(values, ctx)} onClick={next}>Next</Button>
          )}
        </div>
      </div>
    </Shell>
  )
}

/** Route element for `/farms/:farmId/log/:kind/:draftId?`. */
export function LogFlowPage() {
  const { kind } = useParams<{ kind: string }>()
  const flow = flowFor(kind)
  if (!flow) return <Shell><Text>That kind of entry isn’t available.</Text></Shell>
  return <WizardRunner key={flow.kind} flow={flow} />
}
